import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { checkFileSize, formatFileSize } from '../../utils/fileUtils';

const { FiUpload, FiAlertTriangle } = FiIcons;

const FileUpload = ({ 
  onFileUpload, 
  acceptedTypes, 
  uploadText, 
  supportText,
  fileType = 'image'
}) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const validateAndUpload = (file) => {
    if (!file) return;

    const sizeCheck = checkFileSize(file, fileType);
    
    if (!sizeCheck.isValid) {
      if (window.showToast) {
        window.showToast(sizeCheck.message, 'error');
      }
      return;
    }

    if (sizeCheck.level === 'warning') {
      if (window.showToast) {
        window.showToast(sizeCheck.message, 'warning');
      }
    }

    onFileUpload(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    validateAndUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    validateAndUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // 獲取最大文件大小
  const getMaxSize = () => {
    return fileType === 'pdf' ? '100MB' : '50MB';
  };

  return (
    <motion.div
      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-400 transition-colors"
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex flex-col items-center gap-4">
        <motion.div
          className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <SafeIcon icon={FiUpload} className="w-8 h-8 text-purple-600" />
        </motion.div>

        <div>
          <h4 className="text-lg font-medium text-gray-800 mb-2">
            {uploadText}
          </h4>
          <p className="text-sm text-gray-500">
            {supportText}
          </p>

          {/* 文件大小限制提示 */}
          <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-400">
            <SafeIcon icon={FiAlertTriangle} className="w-3 h-3" />
            <span>
              {t('maxFileSize')} {getMaxSize()}
            </span>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        onChange={handleFileChange}
        className="hidden"
      />
    </motion.div>
  );
};

export default FileUpload;